import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { MonitorPlay, LogOut, RefreshCw, FileDown, Loader2, Inbox, Lock, Search, FileSpreadsheet, ArrowUp, ArrowDown, Sparkles, Send, MailPlus, Hourglass, CalendarCheck, BellRing } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { fmt } from '@/lib/pricing';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'globus_admin_token';
const STATUSES = ['eingegangen', 'bestätigt', 'abgelehnt', 'abgeschlossen'];
const STATUS_CLS = {
  eingegangen: 'bg-[#FF6B00]/15 text-[#FF6B00] border-[#FF6B00]/40',
  bestätigt: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40',
  abgelehnt: 'bg-red-500/15 text-red-400 border-red-500/40',
  abgeschlossen: 'bg-[#282C37] text-[#B6BCC8] border-[#3A3F4B]',
};
const KIND = { package: 'Monatspaket', campaign: 'Kampagne', beratung: 'Beratung' };

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '–');
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE') : '–');
const detail = (err, fallback) => { const d = err?.response?.data?.detail; return typeof d === 'string' ? d : fallback; };

function StatusBadge({ status }) {
  return <span className={`inline-flex rounded-full border px-2.5 py-0.5 font-mono-num text-[10px] font-bold uppercase tracking-widest ${STATUS_CLS[status] || STATUS_CLS.abgeschlossen}`}>{status}</span>;
}

function Login({ onLogin }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      setError(detail(err, 'Anmeldung fehlgeschlagen.'));
    } finally { setBusy(false); }
  };
  return (
    <div className="flex min-h-screen items-center justify-center px-5">
      <form onSubmit={submit} className="panel w-full max-w-sm p-8" data-testid="admin-login-form">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#FF6B00]"><Lock className="h-4 w-4 text-white" /></span>
          <div>
            <p className="font-display text-lg font-extrabold tracking-tight">Interner Bereich</p>
            <p className="font-mono-num text-[10px] uppercase tracking-[0.22em] text-[#7D8391]">Auftragsanfragen</p>
          </div>
        </div>
        <label className="label mt-8">Passwort</label>
        <input type="password" data-testid="admin-password-input" value={password} onChange={(e) => setPassword(e.target.value)} className="input" autoFocus autoComplete="current-password" />
        {error && <p className="mt-2 text-sm font-medium text-red-400" data-testid="admin-login-error">{error}</p>}
        <button type="submit" data-testid="admin-login-button" disabled={busy || !password} className="btn-primary mt-6 w-full !py-3 disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Anmelden
        </button>
        <Link to="/" data-testid="admin-back-link" className="mt-6 block text-center text-sm text-[#7D8391] transition-colors hover:text-white">Zurück zur Website</Link>
      </form>
    </div>
  );
}

function AppointmentBlock({ order, onConfirm, busy }) {
  const proposals = [[order.apptDate1, order.apptTime1], [order.apptDate2, order.apptTime2]].filter(([d]) => d);
  const [date, setDate] = useState(order.confirmedApptDate || order.apptDate1 || '');
  const [time, setTime] = useState(order.confirmedApptTime || order.apptTime1 || '');
  return (
    <div data-testid="admin-appointment-block">
      <p className="label">Beratungstermin bestätigen</p>
      {order.confirmedApptDate && (
        <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/15 px-3 py-1 font-mono-num text-[10px] font-bold uppercase tracking-widest text-emerald-400" data-testid="admin-appointment-confirmed">
          <CalendarCheck className="h-3.5 w-3.5" /> {fmtDay(order.confirmedApptDate)} · {order.confirmedApptTime}
        </p>
      )}
      {proposals.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-2">
          {proposals.map(([d, t], i) => (
            <button key={d + t} type="button" data-testid={`admin-appointment-proposal-${i + 1}`} onClick={() => { setDate(d); setTime(t || ''); }}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-[background-color,border-color] duration-200 ${
                date === d && time === (t || '') ? 'border-[#FF6B00] bg-[#FF6B00]/15 text-[#FF6B00]' : 'border-[#282C37] text-[#B6BCC8] hover:border-[#7D8391] hover:text-white'}`}>
              Wunsch {i + 1}: {fmtDay(d)} · {t}
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="admin-appointment-date" className="input w-40 [color-scheme:dark]" />
        <input type="text" value={time} onChange={(e) => setTime(e.target.value)} placeholder="z. B. vormittags (9–12 Uhr)" data-testid="admin-appointment-time" className="input flex-1" />
      </div>
      <button type="button" disabled={busy || !date || !time.trim()} onClick={() => onConfirm(order.id, date, time.trim())} data-testid="admin-appointment-confirm"
        className="btn-primary mt-2 w-full !py-2.5 text-xs disabled:opacity-60">
        <CalendarCheck className="h-3.5 w-3.5" /> Termin bestätigen und Kunde informieren
      </button>
    </div>
  );
}

function ReminderBlock({ order, onRemind, busy }) {
  if (!order.startDate) return null;
  return (
    <div data-testid="admin-reminder-block">
      <p className="label">Erinnerung zur Spotfreigabe</p>
      <p className="t-muted !text-xs">Wird automatisch 7 Tage vor Start verschickt, sobald der Auftrag bestätigt ist.</p>
      {order.reminderSentAt
        ? <p className="mt-2 font-mono-num text-xs text-emerald-400" data-testid="admin-reminder-sent">Gesendet am {fmtDate(order.reminderSentAt)}</p>
        : <p className="mt-2 font-mono-num text-xs text-[#7D8391]" data-testid="admin-reminder-pending">Noch nicht gesendet · Start {fmtDay(order.startDate)}</p>}
      <button type="button" disabled={busy} onClick={() => onRemind(order.id)} data-testid="admin-reminder-send" className="btn-ghost mt-2 w-full !py-2.5 !text-xs disabled:opacity-60">
        <BellRing className="h-3.5 w-3.5" /> Erinnerung jetzt senden
      </button>
    </div>
  );
}

function ReplyForm({ order, onReply, busy }) {
  const [message, setMessage] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    const ok = await onReply(order.id, message.trim());
    if (ok) setMessage('');
  };
  return (
    <form onSubmit={submit} data-testid="admin-reply-form">
      <p className="label">Antwort an {order.email}</p>
      <textarea
        data-testid="admin-reply-input" value={message} onChange={(e) => setMessage(e.target.value)} rows={4} maxLength={4000}
        placeholder="Guten Tag, gern bestätigen wir Ihren Auftrag zum …" className="input !h-auto resize-y"
      />
      <button type="submit" data-testid="admin-reply-submit" disabled={busy || message.trim().length < 5} className="btn-primary mt-2 w-full !py-2.5 text-xs disabled:opacity-60">
        <MailPlus className="h-3.5 w-3.5" /> E-Mail an Kunden senden
      </button>
      <p className="t-muted mt-2 !text-xs">Wird als Notiz am Vorgang protokolliert. Antworten landen bei p.schlueter@globus.net.</p>
    </form>
  );
}

function Comments({ order, onComment, busy }) {
  const [text, setText] = useState('');
  const comments = order.comments || [];
  const submit = async (e) => {
    e.preventDefault();
    const ok = await onComment(order.id, text.trim());
    if (ok) setText('');
  };
  return (
    <div data-testid="admin-comments">
      <p className="label">Interne Notizen</p>
      <div className="space-y-2">
        {comments.length === 0 && <p className="t-muted" data-testid="admin-comments-empty">Noch keine Notizen zu diesem Vorgang.</p>}
        {comments.map((c) => (
          <div key={c.id} className="rounded-xl border border-[#282C37] bg-[#16181D] p-3" data-testid={`admin-comment-${c.id}`}>
            <p className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-[#7D8391]">{fmtDate(c.createdAt)}</p>
            <p className="mt-1 whitespace-pre-wrap text-[#B6BCC8]">{c.text}</p>
          </div>
        ))}
      </div>
      <form onSubmit={submit} className="mt-3">
        <textarea
          data-testid="admin-comment-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Notiz hinzufügen (nur intern sichtbar) …"
          className="input !h-auto resize-y"
        />
        <button type="submit" data-testid="admin-comment-submit" disabled={busy || !text.trim()} className="btn-ghost mt-2 w-full !py-2.5 text-xs disabled:opacity-60">
          <Send className="h-3.5 w-3.5" /> Notiz speichern
        </button>
      </form>
    </div>
  );
}

function OrderDetail({ order, onStatus, onPdf, onComment, onReply, onConfirmAppt, onRemind, busy }) {
  const [notify, setNotify] = useState(true);
  if (!order) return null;
  const rows = [
    ['Unternehmen', order.company], ['Ansprechpartner', order.contactPerson],
    ['Anschrift', `${order.street}, ${order.zip} ${order.city}`], ['E-Mail', order.email], ['Telefon', order.phone],
    ['USt-ID', order.vatId], ['Branche', order.branche], ['Starttermin', order.kind === 'beratung' ? null : fmtDay(order.startDate)],
    ['Wunschtermin 1', order.apptDate1 ? `${fmtDay(order.apptDate1)} · ${order.apptTime1 || ''}` : null],
    ['Wunschtermin 2', order.apptDate2 ? `${fmtDay(order.apptDate2)} · ${order.apptTime2 || ''}` : null],
    ['Bestätigter Termin', order.confirmedApptDate ? `${fmtDay(order.confirmedApptDate)} · ${order.confirmedApptTime || ''}` : null],
    ['Spotproduktion', order.kind === 'beratung' ? null : order.spotProduction], ['Hinweise', order.notes],
  ];
  return (
    <div className="space-y-7 text-sm" data-testid="admin-order-detail">
      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button key={s} type="button" data-testid={`admin-status-${s}`} disabled={busy} onClick={() => onStatus(order.id, s, notify)}
            className={`rounded-full border px-3 py-1.5 font-mono-num text-[10px] font-bold uppercase tracking-widest transition-[background-color,border-color] duration-200 disabled:opacity-60 ${
              order.status === s ? STATUS_CLS[s] : 'border-[#282C37] text-[#7D8391] hover:border-[#7D8391] hover:text-white'}`}>
            {s}
          </button>
        ))}
      </div>
      <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-[#282C37] bg-[#16181D] p-3 text-xs text-[#B6BCC8]" data-testid="admin-notify-label">
        <input type="checkbox" data-testid="admin-notify-toggle" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#FF6B00]" />
        Kunden bei Statuswechsel automatisch per E-Mail informieren (bestätigt, abgelehnt, abgeschlossen)
      </label>
      <dl className="divide-y divide-[#282C37] border-y border-[#282C37]">
        {rows.filter(([, v]) => v).map(([k, v]) => (
          <div key={k} className="grid grid-cols-3 gap-3 py-2.5"><dt className="text-[#7D8391]">{k}</dt><dd className="col-span-2 text-white">{v}</dd></div>
        ))}
      </dl>
      <div>
        <p className="label">Positionen</p>
        <div className="space-y-2">
          {(order.lines || []).map((l) => (
            <div key={`${l.label}-${l.amount}`} className="flex justify-between gap-3"><span className="text-[#B6BCC8]">{l.label}</span><span className={`font-mono-num ${l.amount < 0 ? 'text-emerald-400' : 'text-white'}`}>{l.amount < 0 ? '− ' : ''}{fmt(Math.abs(l.amount))}</span></div>
          ))}
          <div className="flex justify-between border-t border-[#282C37] pt-2 font-bold"><span>Gesamt netto</span><span className="font-mono-num text-[#FF6B00]" data-testid="admin-detail-netto">{fmt(order.totalNetto)}</span></div>
          <div className="flex justify-between text-[#B6BCC8]"><span>Gesamt brutto</span><span className="font-mono-num">{fmt(order.totalBrutto)}</span></div>
        </div>
      </div>
      <div>
        <p className="label">Unterschrift</p>
        <div className="rounded-xl border border-[#282C37] bg-white p-3">
          {order.signatureData ? <img src={order.signatureData} alt="Unterschrift" className="mx-auto max-h-28" data-testid="admin-signature-image" /> : <p className="font-display text-2xl italic text-[#0F1012]" data-testid="admin-signature-typed">{order.typedName}</p>}
        </div>
        <p className="t-muted mt-2">{order.ort}, {fmtDay(order.datum)} · Bedingungen akzeptiert</p>
      </div>
      {order.kind === 'beratung'
        ? <AppointmentBlock order={order} onConfirm={onConfirmAppt} busy={busy} />
        : <ReminderBlock order={order} onRemind={onRemind} busy={busy} />}
      <ReplyForm order={order} onReply={onReply} busy={busy} />
      <Comments order={order} onComment={onComment} busy={busy} />
      <button type="button" onClick={() => onPdf(order.id)} disabled={busy} data-testid="admin-download-pdf" className="btn-ghost w-full !py-3 disabled:opacity-60"><FileDown className="h-4 w-4" /> PDF herunterladen</button>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('alle');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'createdAt', dir: 'desc' });
  const [founder, setFounder] = useState(null);
  const [founderDraft, setFounderDraft] = useState({ total: '', taken: '', mode: 'auto' });
  const [waitlist, setWaitlist] = useState([]);

  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const logout = useCallback(() => { sessionStorage.removeItem(TOKEN_KEY); setToken(null); setSelected(null); }, []);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [ordersRes, founderRes, waitlistRes] = await Promise.all([
        axios.get(`${API}/admin/orders`, auth()),
        axios.get(`${API}/admin/settings/founder`, auth()),
        axios.get(`${API}/admin/waitlist`, auth()),
      ]);
      setOrders(ordersRes.data.orders); setStats(ordersRes.data.stats);
      setFounder(founderRes.data);
      setFounderDraft({ total: String(founderRes.data.total), taken: String(founderRes.data.manualTaken ?? founderRes.data.taken), mode: founderRes.data.mode || 'auto' });
      setWaitlist(waitlistRes.data.entries);
    } catch (err) {
      if (err?.response?.status === 401) { logout(); toast.error('Sitzung abgelaufen – bitte erneut anmelden.'); }
      else toast.error(detail(err, 'Laden fehlgeschlagen.'));
    } finally { setLoading(false); }
  }, [token, auth, logout]);

  useEffect(() => { document.title = 'Intern · Auftragsanfragen'; load(); }, [load]);

  const open = async (id) => {
    try {
      const { data } = await axios.get(`${API}/admin/orders/${id}`, auth());
      setSelected(data);
    } catch (err) { toast.error(detail(err, 'Vorgang konnte nicht geladen werden.')); }
  };

  const setStatus = async (id, status, notify = true) => {
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/admin/orders/${id}/status`, { status, notify }, auth());
      setSelected((s) => (s ? { ...s, status } : s));
      setOrders((os) => os.map((o) => (o.id === id ? { ...o, status } : o)));
      if (data.founder) setFounder(data.founder);
      toast.success(data.mailSent ? `Status: ${status} · Kunde per E-Mail informiert` : `Status: ${status}`);
    } catch (err) { toast.error(detail(err, 'Status konnte nicht geändert werden.')); }
    finally { setBusy(false); }
  };

  const downloadPdf = async (id) => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders/${id}/pdf-token`, auth());
      window.open(`${process.env.REACT_APP_BACKEND_URL}${data.pdfUrl}`, '_blank', 'noopener');
    } catch (err) { toast.error(detail(err, 'PDF konnte nicht geladen werden.')); }
    finally { setBusy(false); }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = filter === 'alle' ? orders : orders.filter((o) => o.status === filter);
    if (q) {
      list = list.filter((o) => [o.company, o.contactPerson, o.email, o.selectionName, o.city, o.branche, o.id]
        .some((v) => (v || '').toLowerCase().includes(q)));
    }
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const av = a[sort.key] ?? '';
      const bv = b[sort.key] ?? '';
      if (typeof av === 'number' || typeof bv === 'number') return ((av || 0) - (bv || 0)) * dir;
      return String(av).localeCompare(String(bv), 'de') * dir;
    });
  }, [orders, filter, search, sort]);

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  const addComment = async (id, text) => {
    if (!text) return false;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/orders/${id}/comments`, { text }, auth());
      setSelected((s) => (s && s.id === id ? { ...s, comments: [...(s.comments || []), data] } : s));
      setOrders((os) => os.map((o) => (o.id === id ? { ...o, comments: [...(o.comments || []), data] } : o)));
      toast.success('Notiz gespeichert.');
      return true;
    } catch (err) { toast.error(detail(err, 'Notiz konnte nicht gespeichert werden.')); return false; }
    finally { setBusy(false); }
  };

  const exportCsv = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/admin/export.csv`, { ...auth(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Auftragsanfragen-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV-Export gestartet.');
    } catch (err) { toast.error(detail(err, 'Export fehlgeschlagen.')); }
    finally { setBusy(false); }
  };

  const replyToOrder = async (id, message) => {
    if (message.length < 5) return false;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/orders/${id}/reply`, { message }, auth());
      setSelected((s) => (s && s.id === id ? { ...s, comments: [...(s.comments || []), data.comment] } : s));
      if (data.sent) toast.success(data.message);
      else toast.error(data.message);
      return data.sent;
    } catch (err) { toast.error(detail(err, 'E-Mail konnte nicht gesendet werden.')); return false; }
    finally { setBusy(false); }
  };

  const confirmAppointment = async (id, date, time) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/orders/${id}/appointment`, { date, time }, auth());
      setSelected((s) => (s && s.id === id
        ? { ...s, confirmedApptDate: data.confirmedApptDate, confirmedApptTime: data.confirmedApptTime, comments: [...(s.comments || []), data.comment] }
        : s));
      if (data.sent) toast.success(data.message); else toast.error(data.message);
    } catch (err) { toast.error(detail(err, 'Termin konnte nicht bestätigt werden.')); }
    finally { setBusy(false); }
  };

  const sendReminder = async (id) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/orders/${id}/reminder`, {}, auth());
      if (data.sent) {
        setSelected((s) => (s && s.id === id ? { ...s, reminderSentAt: data.reminderSentAt } : s));
        toast.success(data.message);
      } else toast.error(data.message);
    } catch (err) { toast.error(detail(err, 'Erinnerung konnte nicht gesendet werden.')); }
    finally { setBusy(false); }
  };

  const offerSlot = async (id) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/waitlist/${id}/notify`, {}, auth());
      if (data.sent) {
        setWaitlist((ws) => ws.map((w) => (w.id === id ? { ...w, status: 'kontaktiert' } : w)));
        toast.success(data.message);
      } else toast.error(data.message);
    } catch (err) { toast.error(detail(err, 'E-Mail konnte nicht gesendet werden.')); }
    finally { setBusy(false); }
  };

  const setWaitlistStatus = async (id, status) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/waitlist/${id}`, { status }, auth());
      setWaitlist((ws) => ws.map((w) => (w.id === id ? { ...w, status } : w)));
      toast.success(`Warteliste: ${status}`);
    } catch (err) { toast.error(detail(err, 'Status konnte nicht geändert werden.')); }
    finally { setBusy(false); }
  };

  const saveFounder = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/admin/settings/founder`, {
        total: Number(founderDraft.total), taken: Number(founderDraft.taken), mode: founderDraft.mode,
      }, auth());
      setFounder(data);
      setFounderDraft({ total: String(data.total), taken: String(data.manualTaken), mode: data.mode });
      toast.success(`Gründungspartner: ${data.remaining} von ${data.total} Plätzen frei.`);
    } catch (err) { toast.error(detail(err, 'Speichern fehlgeschlagen.')); }
    finally { setBusy(false); }
  };

  if (!token) return <Login onLogin={setToken} />;

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-[#282C37] bg-[#0F1012]/90 backdrop-blur-xl">
        <div className="wrap flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-logo">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#FF6B00]"><MonitorPlay className="h-5 w-5 text-white" /></span>
            <span className="font-display leading-tight">
              <span className="block text-sm font-extrabold tracking-tight">GLOBUS Koblenz</span>
              <span className="block font-mono-num text-[10px] uppercase tracking-[0.22em] text-[#7D8391]">Intern · Auftragsanfragen</span>
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <button type="button" onClick={exportCsv} disabled={busy} data-testid="admin-export-csv" className="btn-ghost !px-4 !py-2 text-xs disabled:opacity-60"><FileSpreadsheet className="h-3.5 w-3.5" /> CSV-Export</button>
            <button type="button" onClick={load} data-testid="admin-refresh-button" className="btn-ghost !px-4 !py-2 text-xs"><RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren</button>
            <button type="button" onClick={logout} data-testid="admin-logout-button" className="btn-ghost !px-4 !py-2 text-xs"><LogOut className="h-3.5 w-3.5" /> Abmelden</button>
          </div>
        </div>
      </header>

      <main className="wrap py-10">
        {stats && (
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-[#282C37] bg-[#282C37] lg:grid-cols-4" data-testid="admin-stats">
            {[['Anfragen gesamt', stats.count], ['Offen', stats.open], ['Bestätigt', stats.confirmed], ['Volumen netto', fmt(stats.volumeNetto)]].map(([l, v]) => (
              <div key={l} className="bg-[#16181D] p-6"><p className="font-mono-num text-3xl font-bold tracking-tighter text-white">{v}</p><p className="t-muted mt-1">{l}</p></div>
            ))}
          </div>
        )}

        {founder && (
          <form onSubmit={saveFounder} className="panel mt-6 flex flex-wrap items-end gap-5 p-6" data-testid="admin-founder-panel">
            <div className="mr-auto">
              <p className="flex items-center gap-2 font-mono-num text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF6B00]"><Sparkles className="h-3.5 w-3.5" /> Gründungspartner-Plätze</p>
              <p className="t-muted mt-2 max-w-[52ch]">
                {founderDraft.mode === 'auto'
                  ? `Automatisch: belegt = bestätigte Gründungspartner-Aufträge (aktuell ${founder.confirmed}).`
                  : 'Manuell: Sie pflegen die belegten Plätze selbst.'} Bei 0 freien Plätzen erscheint auf der Website die Warteliste.
              </p>
              <p className="mt-2 font-mono-num text-2xl font-bold text-white" data-testid="admin-founder-remaining">{founder.remaining} / {founder.total} frei</p>
              <div className="mt-3 flex gap-2">
                {[['auto', 'automatisch'], ['manual', 'manuell']].map(([m, label]) => (
                  <button key={m} type="button" data-testid={`admin-founder-mode-${m}`} onClick={() => setFounderDraft((d) => ({ ...d, mode: m }))}
                    className={`rounded-full border px-3 py-1 font-mono-num text-[10px] font-bold uppercase tracking-widest transition-[background-color,border-color] duration-200 ${
                      founderDraft.mode === m ? 'border-[#FF6B00]/40 bg-[#FF6B00]/15 text-[#FF6B00]' : 'border-[#282C37] text-[#7D8391] hover:border-[#7D8391] hover:text-white'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="label" htmlFor="founder-total">Plätze gesamt</label>
              <input id="founder-total" data-testid="admin-founder-total" type="number" min="0" max="999" value={founderDraft.total}
                onChange={(e) => setFounderDraft((d) => ({ ...d, total: e.target.value }))} className="input w-28" />
            </div>
            <div>
              <label className="label" htmlFor="founder-taken">Belegt {founderDraft.mode === 'auto' ? '(auto)' : ''}</label>
              <input id="founder-taken" data-testid="admin-founder-taken" type="number" min="0" max="999" disabled={founderDraft.mode === 'auto'}
                value={founderDraft.mode === 'auto' ? founder.taken : founderDraft.taken}
                onChange={(e) => setFounderDraft((d) => ({ ...d, taken: e.target.value }))} className="input w-28 disabled:opacity-50" />
            </div>
            <button type="submit" disabled={busy} data-testid="admin-founder-save" className="btn-primary !py-3 disabled:opacity-60">Speichern</button>
          </form>
        )}

        {waitlist.length > 0 && (
          <div className="panel mt-6 p-6" data-testid="admin-waitlist-panel">
            <p className="flex items-center gap-2 font-mono-num text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF6B00]"><Hourglass className="h-3.5 w-3.5" /> Warteliste Gründungspartner · {waitlist.filter((w) => w.status === 'offen').length} offen</p>
            <div className="mt-4 divide-y divide-[#282C37]">
              {waitlist.map((w) => (
                <div key={w.id} className="flex flex-wrap items-center gap-4 py-3" data-testid={`admin-waitlist-row-${w.id}`}>
                  <div className="mr-auto">
                    <p className="font-bold text-white">{w.company}</p>
                    <p className="text-xs text-[#7D8391]">{w.contactPerson} · {w.email}{w.phone ? ` · ${w.phone}` : ''}{w.branche ? ` · ${w.branche}` : ''}</p>
                    {w.notes && <p className="mt-1 text-xs text-[#B6BCC8]">{w.notes}</p>}
                  </div>
                  <span className="font-mono-num text-xs text-[#7D8391]">{fmtDate(w.createdAt)}</span>
                  <div className="flex gap-2">
                    <button type="button" disabled={busy} onClick={() => offerSlot(w.id)} data-testid={`admin-waitlist-notify-${w.id}`}
                      className="rounded-full bg-[#FF6B00] px-3 py-1 font-mono-num text-[10px] font-bold uppercase tracking-widest text-white transition-opacity duration-200 hover:opacity-90 disabled:opacity-60">
                      Platz anbieten
                    </button>
                    {['offen', 'kontaktiert', 'erledigt'].map((s) => (
                      <button key={s} type="button" disabled={busy} onClick={() => setWaitlistStatus(w.id, s)} data-testid={`admin-waitlist-${s}-${w.id}`}
                        className={`rounded-full border px-3 py-1 font-mono-num text-[10px] font-bold uppercase tracking-widest transition-[background-color,border-color] duration-200 disabled:opacity-60 ${
                          w.status === s ? 'border-[#FF6B00]/40 bg-[#FF6B00]/15 text-[#FF6B00]' : 'border-[#282C37] text-[#7D8391] hover:border-[#7D8391] hover:text-white'}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="admin-filter">
          {['alle', ...STATUSES].map((s) => (
            <button key={s} type="button" data-testid={`admin-filter-${s}`} onClick={() => setFilter(s)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold capitalize transition-[background-color,color] duration-200 ${filter === s ? 'bg-[#FF6B00] text-white' : 'border border-[#282C37] text-[#B6BCC8] hover:text-white'}`}>
              {s}
            </button>
          ))}
          <div className="relative ml-auto w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7D8391]" />
            <input data-testid="admin-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Suchen: Firma, Person, E-Mail …" className="input !pl-10" />
          </div>
        </div>

        <div className="mt-4 overflow-hidden rounded-2xl border border-[#282C37]">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-sm" data-testid="admin-orders-table">
              <thead>
                <tr className="bg-[#16181D] font-mono-num text-[10px] uppercase tracking-[0.2em] text-[#7D8391]">
                  {[['createdAt', 'Eingang', ''], ['company', 'Unternehmen', ''], ['kind', 'Art', ''], ['selectionName', 'Auswahl', ''], ['startDate', 'Start', ''], ['totalNetto', 'Netto', 'text-right'], ['status', 'Status', '']].map(([key, label, cls]) => (
                    <th key={key} className={`px-5 py-4 font-semibold ${cls}`}>
                      <button type="button" data-testid={`admin-sort-${key}`} onClick={() => toggleSort(key)}
                        className={`inline-flex items-center gap-1 uppercase tracking-[0.2em] transition-colors duration-150 hover:text-white ${sort.key === key ? 'text-[#FF6B00]' : ''}`}>
                        {label}
                        {sort.key === key && (sort.dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-16 text-center text-[#7D8391]" data-testid="admin-empty"><Inbox className="mx-auto mb-3 h-6 w-6" />Keine Anfragen{filter !== 'alle' ? ` mit Status „${filter}“` : ''}{search.trim() ? ` für „${search.trim()}“` : ''}.</td></tr>
                )}
                {visible.map((o) => (
                  <tr key={o.id} data-testid={`admin-order-row-${o.id}`} onClick={() => open(o.id)} className="cursor-pointer border-t border-[#282C37] transition-colors duration-150 hover:bg-[#16181D]">
                    <td className="px-5 py-4 font-mono-num text-xs text-[#B6BCC8]">{fmtDate(o.createdAt)}</td>
                    <td className="px-5 py-4"><p className="font-bold text-white">{o.company}</p><p className="text-xs text-[#7D8391]">{o.contactPerson}</p></td>
                    <td className="px-5 py-4 text-[#B6BCC8]">{KIND[o.kind]}</td>
                    <td className="px-5 py-4 text-white">{o.selectionName}{o.founder ? ' · Gründungspartner' : ''}{o.kind === 'package' ? ` · ${o.founder ? 12 : o.months} Mon.` : ''}</td>
                    <td className="px-5 py-4 font-mono-num text-xs text-[#B6BCC8]">{fmtDay(o.startDate)}</td>
                    <td className="px-5 py-4 text-right font-mono-num font-semibold text-white">{fmt(o.totalNetto)}</td>
                    <td className="px-5 py-4"><StatusBadge status={o.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent className="w-full overflow-y-auto border-[#282C37] bg-[#0F1012] text-white sm:max-w-lg" data-testid="admin-order-sheet">
          <SheetHeader>
            <SheetTitle className="font-display text-2xl font-extrabold tracking-tight text-white">{selected?.company}</SheetTitle>
            <SheetDescription className="font-mono-num text-xs text-[#7D8391]">Vorgang {selected?.id?.slice(0, 8).toUpperCase()} · {fmtDate(selected?.createdAt)}</SheetDescription>
          </SheetHeader>
          <div className="mt-6"><OrderDetail order={selected} onStatus={setStatus} onPdf={downloadPdf} onComment={addComment} onReply={replyToOrder} onConfirmAppt={confirmAppointment} onRemind={sendReminder} busy={busy} /></div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
